
import React, { useState } from 'react';
import { Icons } from './Icons';

interface ResetAppButtonProps {
    className?: string;
    variant?: 'link' | 'button';
}

export const ResetAppButton: React.FC<ResetAppButtonProps> = ({ 
    className = "", 
    variant = 'link' 
}) => {
    const [showConfirm, setShowConfirm] = useState(false);
    const [isResetting, setIsResetting] = useState(false);

    const handleReset = async () => {
        setIsResetting(true);
        try {
            console.log('Initiating application reset...');
            
            // 1. Clear Local and Session Storage
            localStorage.clear();
            sessionStorage.clear();
            
            // 2. Clear Cache Storage
            if ('caches' in window) {
                try {
                    const keys = await caches.keys();
                    await Promise.all(keys.map(key => caches.delete(key)));
                } catch (cacheErr) {
                    console.error('Error clearing cache:', cacheErr);
                }
            }

            // 3. Unregister Service Workers
            if ('serviceWorker' in navigator) {
                try {
                    const regs = await navigator.serviceWorker.getRegistrations();
                    await Promise.all(regs.map(reg => reg.unregister()));
                } catch (swErr) {
                    console.error('Error unregistering service workers:', swErr);
                }
            }

            // 4. Clear IndexedDB
            if ('indexedDB' in window) {
                try {
                    const dbs = await window.indexedDB.databases();
                    dbs.forEach(db => {
                        if (db.name) window.indexedDB.deleteDatabase(db.name);
                    });
                } catch (dbErr) {
                    console.error('Error clearing IndexedDB:', dbErr);
                }
            }

            // 5. Force Hard Reload
            window.location.href = window.location.origin;
        } catch (error) {
            console.error('Fatal error during reset:', error);
            // Even if something fails, try to reload
            window.location.reload();
        }
    };

    if (showConfirm) {
        return (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 max-w-sm w-full shadow-2xl border border-gray-100 dark:border-slate-700">
                    <div className="flex items-center gap-3 text-amber-600 mb-4">
                        <Icons.AlertTriangle className="w-6 h-6" />
                        <h3 className="text-lg font-bold">Reset Application?</h3>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
                        This will clear saved data, log you out, and reload the app. Use this to fix broken states. Continue?
                    </p>
                    <div className="flex gap-3">
                        <button
                            onClick={() => setShowConfirm(false)}
                            disabled={isResetting}
                            className="flex-1 px-4 py-2 text-sm font-bold text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleReset}
                            disabled={isResetting}
                            className="flex-1 px-4 py-2 text-sm font-bold bg-amber-600 hover:bg-amber-700 text-white rounded-xl transition-all flex items-center justify-center gap-2"
                        >
                            {isResetting ? (
                                <Icons.Loader className="w-4 h-4 animate-spin" />
                            ) : (
                                'Confirm Reset'
                            )}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (variant === 'button') {
        return (
            <button
                type="button"
                onClick={() => setShowConfirm(true)}
                className={`text-sm font-medium text-gray-400 hover:text-indigo-600 dark:text-gray-500 dark:hover:text-indigo-400 transition-all flex items-center gap-2 ${className}`}
            >
                <Icons.RefreshCw className="w-4 h-4" />
                Reset Application
            </button>
        );
    }

    return (
        <button
            type="button"
            onClick={() => setShowConfirm(true)}
            className={`text-xs font-medium text-gray-400 hover:text-indigo-600 dark:text-gray-600 dark:hover:text-indigo-400 underline decoration-dotted underline-offset-4 transition-all ${className}`}
        >
            Having issues? Reset App
        </button>
    );
};
